import { emptySplitApi, RootState } from 'shared/store/store'

import { getActiveClient } from 'shared/store/clients/selectors'

import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'
import { ICommonListModel } from 'shared/models/Common'
import {
  IEmailListItem,
  IEmailModel,
  IEmailStatistics,
} from 'Modules/Communication/Email/shared/models/Email'
import { InboxSectionEnum } from 'Modules/Communication/shared/models/Inbox'
import { GET_EMAIL } from 'shared/constants/storeTags'
import { deleteEmailAttachment } from 'Modules/Communication/Email/shared/api/emails.api'

const emailsService = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    fetchEmails: build.query<ICommonListModel<IEmailListItem>, void>({
      queryFn: async (_args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `inbox2/email/messages/query`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          data: {
            clientId: client.id,
            queryStatus: InboxSectionEnum.Received,
            labelId: '',
            dates: {
              deleted: {},
              processed: {},
              received: {},
              sent: {},
            },
            skip: 0,
            take: 5,
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av e-poster.',
          })
          return { error }
        }

        return {
          data: response.data as unknown as ICommonListModel<IEmailListItem>,
        }
      },
    }),
    fetchEmail: build.query<IEmailModel, { id: string }>({
      providesTags: [GET_EMAIL],
      queryFn: async (_args, api, _extra, query) => {
        const response = await query({
          url: `inbox2/email/messages/${_args.id}`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av e-post.',
          })
          return { error }
        }

        return {
          data: response.data as IEmailModel,
        }
      },
    }),

    uploadEmailAttachments: build.mutation<
      void,
      { id: string; attachments: FormData }
    >({
      queryFn: async ({ id, attachments }, api, _extra, query) => {
        const response = await query({
          url: `inbox2/email/messages/${id}/attachments`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          data: attachments,
        })

        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError:
              'En feil har oppstått i opplasting av e-postvedlegg.',
          })
          return { error }
        }
        return { data: undefined }
      },
      invalidatesTags: [GET_EMAIL],
    }),

    deleteEmailAttachment: build.mutation<
      void,
      { messageId: string; attachmentId: string }
    >({
      queryFn: async ({ messageId, attachmentId }, api, _extra, query) => {
        const response = await query({
          url: `inbox2/email/messages/${messageId}/attachments/${attachmentId}`,
          method: 'DELETE',
          headers: {
            'x-api-version': '1.0',
          },
        })

        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i sletting av e-postvedlegg.',
          })
          return { error }
        }
        return { data: undefined }
      },
      invalidatesTags: [GET_EMAIL],
    }),

    fetchUnreadEmailCount: build.query<IEmailStatistics, void>({
      queryFn: async (_args, api, _extra, query) => {
        const state = api.getState() as RootState
        const client = getActiveClient(state)

        if (!client) throw Error('No client available')

        const response = await query({
          url: `inbox2/email/messages/clients/${client.id}/statistics`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError: 'En feil har oppstått i henting av e-poster.',
          })
          return { error }
        }

        return {
          data: response.data as unknown as IEmailStatistics,
        }
      },
    }),
  }),
  overrideExisting: false,
})

export default emailsService
export const {
  useFetchEmailsQuery,
  useFetchUnreadEmailCountQuery,
  useFetchEmailQuery,
  useUploadEmailAttachmentsMutation,
  useDeleteEmailAttachmentMutation,
} = emailsService
